
import { useState, useEffect, useRef } from 'react';
import { Mail, MapPin, Phone, Send } from 'lucide-react';
import { toast } from 'sonner';
import emailjs from 'emailjs-com';

const Contact = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
    service: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const sectionRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    
    return () => observer.disconnect();
  }, []);
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Replace these with your actual EmailJS service ID, template ID, and user ID
      // You'll need to sign up at https://www.emailjs.com/ and set these up
      const serviceId = 'YOUR_EMAILJS_SERVICE_ID';
      const templateId = 'YOUR_EMAILJS_TEMPLATE_ID';
      const userId = 'YOUR_EMAILJS_USER_ID';
      
      const templateParams = {
        to_email: '22jlbaker@gmail.com',
        from_name: formData.name,
        from_email: formData.email,
        service_type: formData.service,
        message: formData.message
      };
      
      await emailjs.send(serviceId, templateId, templateParams, userId);
      
      toast.success('Message sent successfully! We\'ll be in touch soon.');
      
      setFormData({
        name: '',
        email: '',
        message: '',
        service: ''
      });
    } catch (error) {
      console.error('Error sending email:', error);
      toast.error('Failed to send message. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <section id="contact" ref={sectionRef} className="py-20 px-6 bg-gradient-to-b from-muted/30 to-background">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16 space-y-4">
          <div className="inline-block rounded-full bg-primary/20 px-3 py-1 text-sm font-medium text-primary">
            Get In Touch
          </div>
          <h2 className="text-3xl md:text-4xl font-bold">
            Ready to <span className="tech-gradient-text">Transform</span> Your Digital Presence?
          </h2>
          <p className="text-foreground/70 max-w-2xl mx-auto">
            Contact us today to discuss your project. Our team is ready to help you achieve your technology goals.
          </p>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          <div className={`lg:col-span-2 space-y-8 ${isVisible ? 'animate-fade-in' : 'opacity-0'}`}>
            <div className="glass-card p-6 rounded-lg">
              <div className="flex items-start space-x-4">
                <div className="bg-accent/20 p-3 rounded-full">
                  <MapPin className="h-6 w-6 text-accent" />
                </div>
                <div>
                  <h3 className="font-bold text-lg">Our Location</h3>
                  <p className="text-foreground/70">Mizzou</p>
                  <p className="text-foreground/70">Columbia, MO</p>
                </div>
              </div>
            </div>
            
            <div className="glass-card p-6 rounded-lg">
              <div className="flex items-start space-x-4">
                <div className="bg-primary/20 p-3 rounded-full">
                  <Mail className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-bold text-lg">Email Us</h3>
                  <p className="text-foreground/70">
                    <a href="mailto:22jlbaker@gmail.com" className="hover:text-primary transition-colors">
                      22jlbaker@gmail.com
                    </a>
                  </p>
                </div>
              </div>
            </div>
            
            <div className="glass-card p-6 rounded-lg">
              <div className="flex items-start space-x-4">
                <div className="bg-secondary/20 p-3 rounded-full">
                  <Phone className="h-6 w-6 text-secondary" />
                </div>
                <div>
                  <h3 className="font-bold text-lg">Call Us</h3>
                  <p className="text-foreground/70">
                    <a href="tel:6365777481" className="hover:text-secondary transition-colors">
                      (636) 577-7481
                    </a>
                  </p>
                  <p className="text-foreground/70">Mon-Fri, 9AM-6PM CT</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className={`lg:col-span-3 glass-card p-8 rounded-lg shadow-lg ${isVisible ? 'animate-fade-in-slow' : 'opacity-0'}`}>
            <h3 className="text-2xl font-bold mb-6">Send Us a Message</h3>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-foreground/70 mb-1">
                    Your Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50"
                    placeholder="John Doe"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground/70 mb-1">
                    Your Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50"
                    placeholder="john@example.com"
                  />
                </div>
              </div>
              
              <div>
                <label htmlFor="service" className="block text-sm font-medium text-foreground/70 mb-1">
                  Service You're Interested In
                </label>
                <select
                  id="service"
                  name="service"
                  value={formData.service}
                  onChange={handleChange}
                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50"
                >
                  <option value="" disabled>Select a service</option>
                  <option value="web-design">Web Design</option>
                  <option value="seo">SEO Optimization</option>
                  <option value="graphic-design">Graphic Design</option>
                  <option value="software">Software Creation</option>
                  <option value="server">Server Maintenance</option>
                  <option value="ecommerce">E-commerce Solutions</option>
                  <option value="other">Other</option>
                </select>
              </div>
              
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-foreground/70 mb-1">
                  Your Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={4}
                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50"
                  placeholder="Tell us about your project..."
                ></textarea>
              </div>
              
              <button 
                type="submit" 
                className="btn-primary w-full flex items-center justify-center space-x-2"
                disabled={isSubmitting}
              >
                <span>{isSubmitting ? 'Sending...' : 'Send Message'}</span>
                <Send className="h-4 w-4" />
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
